/**
 * ShaderBackground - Three.js fluid dark nebula effect
 *
 * Creates an immersive animated background using WebGL shaders.
 * Responds to mouse movement for interactive depth.
 */

import { useEffect, useRef, useCallback } from 'react'

// Fragment shader for dark nebula effect
const FRAGMENT_SHADER = `
  uniform float u_time;
  uniform vec2 u_resolution;
  uniform vec2 u_mouse;

  // Simple noise function
  float random(in vec2 _st) {
    return fract(sin(dot(_st.xy, vec2(12.9898, 78.233))) * 43758.5453123);
  }

  // Value noise
  float noise(in vec2 _st) {
    vec2 i = floor(_st);
    vec2 f = fract(_st);
    float a = random(i);
    float b = random(i + vec2(1.0, 0.0));
    float c = random(i + vec2(0.0, 1.0));
    float d = random(i + vec2(1.0, 1.0));
    vec2 u = f * f * (3.0 - 2.0 * f);
    return mix(a, b, u.x) + (c - a) * u.y * (1.0 - u.x) + (d - b) * u.x * u.y;
  }

  // Fractal Brownian Motion
  float fbm(vec2 st) {
    float value = 0.0;
    float amplitude = 0.5;
    for (int i = 0; i < 5; i++) {
      value += amplitude * noise(st);
      st *= 2.0;
      amplitude *= 0.5;
    }
    return value;
  }

  void main() {
    vec2 st = gl_FragCoord.xy / u_resolution.xy;
    st.x *= u_resolution.x / u_resolution.y;

    vec3 color = vec3(0.0);

    // Fluid movement based on time
    vec2 pos = st * 3.0;
    float t = u_time * 0.05;

    // Multiple layers of noise for depth
    float n1 = fbm(pos + vec2(t, t * 0.5));
    float n2 = fbm(pos * 2.0 - vec2(t * 0.7, t * 0.3));
    float n3 = fbm(pos * 0.5 + vec2(t * 0.2, -t * 0.4));

    float combined = n1 * 0.5 + n2 * 0.3 + n3 * 0.2;

    // Mouse interaction - subtle light effect
    float mouseDist = distance(st, u_mouse);
    float mouseInfluence = smoothstep(0.5, 0.0, mouseDist) * 0.15;
    combined += mouseInfluence;

    // Color mapping: Deep space with cyan accents
    vec3 darkBlue = vec3(0.02, 0.02, 0.05);
    vec3 midBlue = vec3(0.0, 0.08, 0.15);
    vec3 accent = vec3(0.0, 0.94, 1.0); // #00f0ff

    color = mix(darkBlue, midBlue, combined);

    // Add subtle cyan glow in bright areas
    if (combined > 0.55) {
      color += accent * (combined - 0.55) * 0.3;
    }

    // Vignette effect
    float vignette = 1.0 - smoothstep(0.4, 1.4, length(st - 0.5) * 1.5);
    color *= vignette;

    gl_FragColor = vec4(color, 1.0);
  }
`

const VERTEX_SHADER = `
  void main() {
    gl_Position = vec4(position, 1.0);
  }
`

interface ShaderBackgroundProps {
  className?: string
  opacity?: number
}

export function ShaderBackground({ className = '', opacity = 0.6 }: ShaderBackgroundProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const rendererRef = useRef<any>(null)
  const animationRef = useRef<number>(0)
  const mouseRef = useRef({ x: 0.5, y: 0.5 })

  const handleMouseMove = useCallback((e: MouseEvent) => {
    mouseRef.current = {
      x: e.clientX / window.innerWidth,
      y: 1.0 - e.clientY / window.innerHeight,
    }
  }, [])

  useEffect(() => {
    // Check for reduced motion preference
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    if (prefersReducedMotion) {
      return
    }

    const container = containerRef.current
    if (!container) return

    // Dynamically import Three.js to reduce initial bundle
    const initThree = async () => {
      try {
        const THREE = await import('three')

        const scene = new THREE.Scene()
        const camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1)
        const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: false })

        renderer.setSize(window.innerWidth, window.innerHeight)
        renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
        container.appendChild(renderer.domElement)
        rendererRef.current = renderer

        const geometry = new THREE.PlaneGeometry(2, 2)

        const uniforms = {
          u_time: { value: 0.0 },
          u_resolution: { value: new THREE.Vector2(window.innerWidth, window.innerHeight) },
          u_mouse: { value: new THREE.Vector2(0.5, 0.5) },
        }

        const material = new THREE.ShaderMaterial({
          uniforms,
          vertexShader: VERTEX_SHADER,
          fragmentShader: FRAGMENT_SHADER,
        })

        const plane = new THREE.Mesh(geometry, material)
        scene.add(plane)

        // Animation loop
        const animate = () => {
          animationRef.current = requestAnimationFrame(animate)
          uniforms.u_time.value += 0.016 // ~60fps timing
          uniforms.u_mouse.value.x = mouseRef.current.x
          uniforms.u_mouse.value.y = mouseRef.current.y
          renderer.render(scene, camera)
        }

        animate()

        // Resize handler
        const handleResize = () => {
          renderer.setSize(window.innerWidth, window.innerHeight)
          uniforms.u_resolution.value.x = window.innerWidth
          uniforms.u_resolution.value.y = window.innerHeight
        }

        window.addEventListener('resize', handleResize)
        window.addEventListener('mousemove', handleMouseMove)

        return () => {
          window.removeEventListener('resize', handleResize)
          window.removeEventListener('mousemove', handleMouseMove)
          cancelAnimationFrame(animationRef.current)
          renderer.dispose()
          geometry.dispose()
          material.dispose()
          container.removeChild(renderer.domElement)
        }
      } catch (error) {
        console.warn('Three.js shader background failed to initialize:', error)
      }
    }

    const cleanup = initThree()

    return () => {
      cleanup.then(cleanupFn => cleanupFn?.())
    }
  }, [handleMouseMove])

  return (
    <div
      ref={containerRef}
      className={className}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: -1,
        opacity,
        pointerEvents: 'none',
      }}
      aria-hidden="true"
    />
  )
}

export default ShaderBackground
